import { useCallback, useEffect, useRef, useState } from 'react';

const RESET_DELAY_MS = 2000;

export type UseCopyToClipboardOptions = {
    resetDelayMs?: number;
};

export function useCopyToClipboard(options: UseCopyToClipboardOptions = {}) {
    const { resetDelayMs = RESET_DELAY_MS } = options;
    const [copied, setCopied] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const copy = useCallback(
        async (text: string) => {
            const value = text?.trim();
            if (typeof navigator?.clipboard?.writeText !== 'function' || !value) return;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            try {
                await navigator.clipboard.writeText(value);
                setCopied(true);
                timeoutRef.current = setTimeout(() => {
                    setCopied(false);
                    timeoutRef.current = null;
                }, resetDelayMs);
            } catch {
                setCopied(false);
            }
        },
        [resetDelayMs]
    );

    return { copy, copied };
}
